import { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    CircularProgress,
    Modal,
    ModalClose,
    ModalDialog,
    Typography,
} from "@mui/joy";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed } from "lucide-react";

import { Button, IconButton } from "../../../../../shared";
import {
    DEFAULT_MAP_CENTER,
    getCurrentPosition,
    reverseGeocode,
    type PickedAddress,
} from "../../../../../utils/geocode";

// Fix default marker icons broken by CRA webpack asset handling
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const DefaultIcon = L.icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

/** Soft map tint aligned with brand accent (#9ebce2). */
const MAP_BRAND_FILTER =
    "grayscale(12%) sepia(18%) hue-rotate(175deg) saturate(1.15) brightness(1.02) contrast(0.96)";
const MAP_BRAND_BG = "#9ebce2";

interface AddressMapPickerProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (address: PickedAddress) => void;
    initialLat?: number;
    initialLon?: number;
}

export default function AddressMapPicker({
    open,
    onClose,
    onConfirm,
    initialLat,
    initialLon,
}: AddressMapPickerProps) {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    const markerRef = useRef<L.Marker | null>(null);

    const [pin, setPin] = useState({
        lat: initialLat ?? DEFAULT_MAP_CENTER.lat,
        lon: initialLon ?? DEFAULT_MAP_CENTER.lon,
    });
    const [preview, setPreview] = useState<PickedAddress | null>(null);
    const [loading, setLoading] = useState(false);
    const [locating, setLocating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [locationHint, setLocationHint] = useState<string | null>(null);
    const [mapReady, setMapReady] = useState(false);

    const movePinTo = useCallback((lat: number, lon: number, zoom = 16) => {
        setPin({ lat, lon });
        const map = mapRef.current;
        const marker = markerRef.current;
        if (marker) {
            marker.setLatLng([lat, lon]);
        }
        if (map) {
            map.setView([lat, lon], zoom, { animate: true });
            map.invalidateSize({ animate: false });
        }
    }, []);

    const goToCurrentLocation = useCallback(
        async (opts?: { silent?: boolean }) => {
            const silent = opts?.silent ?? false;
            setLocating(true);
            if (!silent) {
                setLocationHint(null);
            }
            try {
                const position = await getCurrentPosition();
                movePinTo(position.lat, position.lon, 17);
                setLocationHint("Centered on your current location");
            } catch (err: any) {
                if (!silent) {
                    setLocationHint(
                        err?.message ||
                            "Could not use current location — showing default map area",
                    );
                }
            } finally {
                setLocating(false);
            }
        },
        [movePinTo],
    );

    useEffect(() => {
        if (!open) {
            setMapReady(false);
            return;
        }

        const fallbackLat = initialLat ?? DEFAULT_MAP_CENTER.lat;
        const fallbackLon = initialLon ?? DEFAULT_MAP_CENTER.lon;
        setPin({ lat: fallbackLat, lon: fallbackLon });
        setPreview(null);
        setError(null);
        setLocationHint(
            initialLat != null && initialLon != null
                ? null
                : "Finding your current location…",
        );

        const readyTimer = window.setTimeout(() => setMapReady(true), 50);
        return () => {
            window.clearTimeout(readyTimer);
            setMapReady(false);
        };
    }, [open, initialLat, initialLon]);

    useEffect(() => {
        if (!open || !mapReady || !mapContainerRef.current) {
            return;
        }

        const startLat = pin.lat;
        const startLon = pin.lon;

        const map = L.map(mapContainerRef.current, {
            center: [startLat, startLon],
            zoom: DEFAULT_MAP_CENTER.zoom,
            zoomControl: true,
            attributionControl: false,
        });
        mapRef.current = map;

        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
            maxZoom: 19,
        }).addTo(map);

        const marker = L.marker([startLat, startLon], { draggable: true }).addTo(map);
        markerRef.current = marker;

        const updatePin = (lat: number, lon: number) => {
            setPin({ lat, lon });
            marker.setLatLng([lat, lon]);
        };

        map.on("click", (event: L.LeafletMouseEvent) => {
            updatePin(event.latlng.lat, event.latlng.lng);
        });

        marker.on("dragend", () => {
            const { lat, lng } = marker.getLatLng();
            updatePin(lat, lng);
        });

        const refreshSize = () => {
            map.invalidateSize({ animate: false });
        };

        const timers = [50, 150, 300, 500].map((ms) =>
            window.setTimeout(refreshSize, ms),
        );
        window.addEventListener("resize", refreshSize);

        // Prefer device GPS when no explicit initial coords were provided
        if (initialLat == null || initialLon == null) {
            void goToCurrentLocation({ silent: false });
        }

        return () => {
            timers.forEach((id) => window.clearTimeout(id));
            window.removeEventListener("resize", refreshSize);
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mapReady]);

    useEffect(() => {
        if (!open) {
            return;
        }

        let cancelled = false;
        const timer = window.setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const address = await reverseGeocode(pin.lat, pin.lon);
                if (!cancelled) {
                    setPreview(address);
                }
            } catch (err: any) {
                if (!cancelled) {
                    setPreview(null);
                    setError(err?.message || "Could not resolve address");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }, 350);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [open, pin.lat, pin.lon]);

    const handleUseLocation = () => {
        if (!preview) {
            return;
        }
        onConfirm(preview);
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog
                variant="outlined"
                sx={{
                    width: "min(720px, 94vw)",
                    maxWidth: 720,
                    p: 0,
                    overflow: "hidden",
                    borderRadius: "4px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        px: 2,
                        pt: 1.75,
                        pb: 1,
                    }}
                >
                    <Box sx={{ minWidth: 0, pr: 2 }}>
                        <Typography level="title-md" sx={{ fontWeight: 700 }}>
                            Pick location on map
                        </Typography>
                        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                            Starts from your current location. Tap or drag the pin to adjust.
                        </Typography>
                    </Box>
                    <ModalClose />
                </Box>

                <Box
                    sx={{
                        position: "relative",
                        height: 320,
                        width: "100%",
                        minHeight: 320,
                        bgcolor: MAP_BRAND_BG,
                        zIndex: 0,
                        isolation: "isolate",
                    }}
                >
                    {mapReady ? (
                        <Box
                            ref={mapContainerRef}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                height: "100%",
                                width: "100%",
                                zIndex: 0,
                                "& .leaflet-container": {
                                    height: "100%",
                                    width: "100%",
                                    zIndex: 0,
                                    fontFamily: "inherit",
                                    background: MAP_BRAND_BG,
                                },
                                // Brand-tint tiles only — leave markers/controls unfiltered
                                "& .leaflet-tile-pane": {
                                    filter: MAP_BRAND_FILTER,
                                },
                                "& .leaflet-pane, & .leaflet-top, & .leaflet-bottom": {
                                    zIndex: "auto",
                                },
                                "& .leaflet-control-container": {
                                    zIndex: 1,
                                },
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size="sm" />
                        </Box>
                    )}

                    <IconButton
                        type="button"
                        size="sm"
                        variant="soft"
                        color="primary"
                        aria-label="Use current location"
                        disabled={locating}
                        onClick={() => void goToCurrentLocation()}
                        sx={{
                            position: "absolute",
                            right: 12,
                            bottom: 12,
                            zIndex: 2,
                            borderRadius: "4px",
                            boxShadow: "sm",
                            bgcolor: "background.surface",
                        }}
                    >
                        {locating ? (
                            <CircularProgress size="sm" />
                        ) : (
                            <LocateFixed size={18} />
                        )}
                    </IconButton>
                </Box>

                <Box sx={{ px: 2, py: 1.5 }}>
                    {locationHint ? (
                        <Typography
                            level="body-xs"
                            sx={{ color: "text.tertiary", mb: 0.75 }}
                        >
                            {locationHint}
                        </Typography>
                    ) : null}

                    {loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size="sm" />
                            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                                Looking up address…
                            </Typography>
                        </Box>
                    ) : error ? (
                        <Typography level="body-sm" color="danger">
                            {error}
                        </Typography>
                    ) : preview ? (
                        <Box>
                            <Typography
                                level="body-xs"
                                sx={{
                                    textTransform: "uppercase",
                                    fontWeight: "xl",
                                    letterSpacing: "0.5px",
                                    color: "text.tertiary",
                                    mb: 0.35,
                                }}
                            >
                                Selected address
                            </Typography>
                            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                {preview.formatted}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                            Move the pin to a property location.
                        </Typography>
                    )}

                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                            mt: 1.5,
                            flexWrap: "wrap",
                        }}
                    >
                        <Button
                            type="button"
                            variant="outlined"
                            color="neutral"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="soft"
                            color="primary"
                            disabled={!preview || loading}
                            onClick={handleUseLocation}
                        >
                            Use this address
                        </Button>
                    </Box>
                </Box>
            </ModalDialog>
        </Modal>
    );
}
